import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FooterImage from '../assets/imageFooter.png';
import emailjs from 'emailjs-com';
import { getCountryCode } from '../Firebase';
import './Contactus.css';

const GREECE_CONTACT_API_URL =
    'https://2qktdayi3ljgopbdul7zolylam0kpcmo.lambda-url.eu-central-1.on.aws/';

function readMobiCountry() {
    try {
        const raw = sessionStorage.getItem('AI_MobiLoginResponse');
        if (!raw) return '';
        const d = JSON.parse(raw);
        return String(d?.Result?.Country || '').toUpperCase();
    } catch {
        return '';
    }
}

function IconUser() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M12 11.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 20.5v-.5a7 7 0 0114 0v.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconMail() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M4 6h16v12H4V6z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconMessage() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M4 5h16v11H9l-4 3v-3H4V5z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 10h8M8 13h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
    );
}

function IconChevronLeft() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ContactUs() {
    const navigate = useNavigate();
    const [geoCountry, setGeoCountry] = useState('');
    const mobiCountry = useMemo(() => readMobiCountry(), []);

    useEffect(() => {
        getCountryCode()
            .then((c) => setGeoCountry((c || '').trim().toUpperCase()))
            .catch(() => setGeoCountry('OTHER'));
    }, []);

    const isGreece = geoCountry === 'GR' || mobiCountry === 'GR';

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [progress, setProgress] = useState(0);
    const [progressSuccess, setProgressSuccess] = useState(false);
    const progressTimersRef = useRef([]);

    const clearProgressTimers = useCallback(() => {
        progressTimersRef.current.forEach(clearTimeout);
        progressTimersRef.current = [];
    }, []);

    const startSendProgress = useCallback(() => {
        clearProgressTimers();
        setProgressSuccess(false);
        setProgress(4);
        const stages = [
            [26, 70],
            [48, 200],
            [64, 400],
            [78, 620],
            [90, 880]
        ];
        stages.forEach(([pct, delay]) => {
            const id = setTimeout(() => setProgress(pct), delay);
            progressTimersRef.current.push(id);
        });
    }, [clearProgressTimers]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const submitGreeceContact = useCallback(() => {
        const body = {
            domain: 'aiavatar.mobi',
            formName: 'Contact',
            Subject: 'Contact form submission',
            values: {
                name: formData.name,
                email: formData.email,
                message: formData.message
            }
        };
        return fetch(GREECE_CONTACT_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).then((res) => {
            if (!res.ok) {
                return res.text().then((text) => {
                    throw new Error(text || `HTTP ${res.status}`);
                });
            }
            return res.text().then((t) => {
                const trimmed = (t || '').trim();
                if (!trimmed) return {};
                try {
                    return JSON.parse(trimmed);
                } catch {
                    return {};
                }
            });
        });
    }, [formData.name, formData.email, formData.message]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);
        startSendProgress();

        const sendPromise = isGreece
            ? submitGreeceContact()
            : emailjs.send(
                  'aiavatar',
                  'template_8f9ze7a',
                  formData,
                  'bqX9OFBB8HdLWSXUh'
              );

        sendPromise
            .then(() => {
                clearProgressTimers();
                setProgress(100);
                setProgressSuccess(true);
                setFormData({ name: '', email: '', message: '' });
                setMessage({ type: 'success', text: 'Message sent. We will get back to you soon.' });
                setTimeout(() => {
                    setProgress(0);
                    setProgressSuccess(false);
                }, 1100);
            })
            .catch((error) => {
                console.error('Contact send error:', error);
                clearProgressTimers();
                setProgress(0);
                setProgressSuccess(false);
                setMessage({ type: 'error', text: 'Something went wrong. Please try again in a moment.' });
            })
            .finally(() => {
                setLoading(false);
                setTimeout(() => setMessage(null), 6000);
            });
    };

    const progressFillClass = [
        'contact-progress-fill',
        progressSuccess ? 'contact-progress-success' : ''
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            className="contact-us-page"
            style={{
                background: `linear-gradient(0deg,rgba(0,0,0,.6),rgba(0,0,0,.6)),url(${FooterImage})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
            }}
        >
            <div className="contact-us-inner">
                <div className="contact-card-shell">
                    <div className="contact-card">
                        <div className="contact-progress-track" aria-hidden>
                            <div className={progressFillClass} style={{ width: `${progress}%` }} />
                        </div>
                        <div className="contact-card-body">
                            <div className="contact-card-top">
                                <button
                                    type="button"
                                    className="contact-back-btn"
                                    onClick={() => navigate(-1)}
                                >
                                    <IconChevronLeft />
                                    Back
                                </button>
                            </div>
                            <header className="contact-heading">
                                <h1 className="contact-title">Contact us</h1>
                                <p className="contact-subtitle">Questions or feedback? Send a message.</p>
                            </header>

                            {message && (
                                <div
                                    className={`contact-feedback contact-feedback--${message.type === 'success' ? 'success' : 'error'}`}
                                    role="status"
                                >
                                    {message.text}
                                </div>
                            )}

                            <form
                                className="contact-form"
                                onSubmit={handleSubmit}
                                aria-busy={loading}
                            >
                                <div className="contact-field">
                                    <label className="contact-label" htmlFor="contact-name">
                                        Full Name
                                    </label>
                                    <div className="contact-input-row">
                                        <span className="contact-input-icon">
                                            <IconUser />
                                        </span>
                                        <input
                                            id="contact-name"
                                            className="contact-input"
                                            placeholder="Your full name"
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            autoComplete="name"
                                        />
                                    </div>
                                </div>

                                <div className="contact-field">
                                    <label className="contact-label" htmlFor="contact-email">
                                        Email
                                    </label>
                                    <div className="contact-input-row">
                                        <span className="contact-input-icon">
                                            <IconMail />
                                        </span>
                                        <input
                                            id="contact-email"
                                            className="contact-input"
                                            placeholder="you@example.com"
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                <div className="contact-field">
                                    <label className="contact-label" htmlFor="contact-message">
                                        Message
                                    </label>
                                    <div className="contact-input-row">
                                        <span className="contact-input-icon contact-input-icon--top">
                                            <IconMessage />
                                        </span>
                                        <textarea
                                            id="contact-message"
                                            className="contact-textarea"
                                            placeholder="How can we help?"
                                            name="message"
                                            rows={4}
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="contact-submit-wrap">
                                    <button type="submit" className="contact-submit-btn" disabled={loading}>
                                        {loading ? (
                                            <span className="contact-submit-inner">
                                                <span className="contact-submit-spinner" aria-hidden />
                                                Sending…
                                            </span>
                                        ) : (
                                            'Send message'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContactUs;
